import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

const highlights = [
  { icon: 'ri-eye-line', value: 'Resultados', desc: 'visibles en minutos' },
  { icon: 'ri-flask-line', value: 'Formulación', desc: 'propia y controlada' },
  { icon: 'ri-award-line', value: 'Estándares', desc: 'internacionales' },
  { icon: 'ri-map-pin-line', value: 'Adaptado', desc: 'a yacimientos venezolanos' },
];

const productImages = [
  {
    src: 'https://static.readdy.ai/image/51f676c9cc2d72a77723e075d004b9cd/42862eddf739d32b013cdf187dde0b32.jpeg',
    alt: 'Formulación de aditivos en balanza de precisión',
    label: 'Formulación',
    icon: 'ri-flask-line',
  },
  {
    src: 'https://static.readdy.ai/image/51f676c9cc2d72a77723e075d004b9cd/5b41f56eb29c2520b2a33d683b226285.jpeg',
    alt: 'Muestras numeradas en laboratorio de evaluación',
    label: 'Evaluación',
    icon: 'ri-test-tube-line',
  },
  {
    src: 'https://static.readdy.ai/image/51f676c9cc2d72a77723e075d004b9cd/8929f81f3f9f371d8e3ea59c4a3fe151.jpeg',
    alt: 'Tanques IBC cargados en camión para distribución',
    label: 'Logística',
    icon: 'ri-truck-line',
  },
  {
    src: 'https://static.readdy.ai/image/51f676c9cc2d72a77723e075d004b9cd/b06b035c67c7e38bb12c06ea26a7b3e2.jpeg',
    alt: 'Planta industrial con equipo de operaciones y IBCs',
    label: 'Operaciones',
    icon: 'ri-building-4-line',
  },
];

export default function LabVideoSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.12 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="w-full py-20 md:py-28 bg-background-100 overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 md:px-6">

        {/* ── Top label ── */}
        <div className={`flex items-center gap-3 mb-4 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
          <div className="w-8 h-px bg-accent-500" />
          <span className="text-accent-600 text-xs font-semibold tracking-widest uppercase">Nuestros Productos en Acción</span>
        </div>

        {/* ── Headline ── */}
        <div className={`mb-12 transition-all duration-700 delay-100 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-5'}`}>
          <h2 className="font-heading font-bold text-3xl md:text-4xl lg:text-5xl text-foreground-950 leading-tight max-w-3xl">
            La química que{' '}
            <span className="text-primary-500">transforma</span>{' '}
            la industria
          </h2>
          <p className="mt-4 text-foreground-500 text-base md:text-lg max-w-2xl leading-relaxed">
            Cada producto que fabricamos pasa por pruebas rigurosas antes de llegar al campo. Esto es lo que hace la diferencia
            entre un químico cualquiera y una solución <strong className="text-foreground-700">diseñada para rendir</strong>.
          </p>
        </div>

        {/* ── Main layout: static image grid left + content right ── */}
        <div className="flex flex-col lg:flex-row gap-10 lg:gap-16 items-center">

          {/* ── Static image grid ── */}
          <div className={`w-full lg:w-[55%] flex-shrink-0 transition-all duration-800 delay-150 ${visible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'}`}>
            <div className="grid grid-cols-2 gap-3 h-[480px] md:h-[520px]">
              {productImages.map((img, i) => (
                <div key={i} className="relative rounded-2xl overflow-hidden bg-foreground-950 border border-background-200 group cursor-pointer">
                  <img
                    src={img.src}
                    alt={img.alt}
                    className="w-full h-full object-cover object-top"
                  />
                  {/* Gradient overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/45 via-transparent to-transparent" />
                  {/* Label badge */}
                  <div className="absolute bottom-3 left-3">
                    <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-black/40 backdrop-blur-sm text-white text-xs font-semibold border border-white/15">
                      <i className={`${img.icon} text-accent-400`}></i>
                      {img.label}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* ── Right content ── */}
          <div className={`flex-1 transition-all duration-800 delay-250 ${visible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'}`}>

            {/* Claim impactante */}
            <div className="p-6 rounded-2xl bg-primary-500 text-white mb-6">
              <p className="text-lg md:text-xl font-heading font-bold leading-snug mb-2">
                &quot;No vendemos promesas. Vendemos resultados que puedes ver.&quot;
              </p>
              <p className="text-primary-100 text-sm leading-relaxed">
                Nuestro equipo de químicos evalúa cada fórmula en condiciones que replican exactamente
                las del yacimiento — temperatura, presión, salinidad y composición del crudo.
              </p>
            </div>

            {/* Highlights grid */}
            <div className="grid grid-cols-2 gap-3 mb-6">
              {highlights.map((h) => (
                <div key={h.value} className="flex items-start gap-3 p-4 rounded-xl bg-background-50 border border-background-200">
                  <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-accent-100 flex-shrink-0">
                    <i className={`${h.icon} text-accent-600 text-base`}></i>
                  </div>
                  <div>
                    <p className="text-foreground-900 text-sm font-semibold">{h.value}</p>
                    <p className="text-foreground-500 text-xs mt-0.5">{h.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Extra copy */}
            <p className="text-foreground-500 text-sm leading-relaxed mb-6">
              Desde inhibidores de corrosión hasta demulsificantes de alto rendimiento, cada producto
              lleva el respaldo de pruebas documentadas y años de operación en campos venezolanos
              y proyectos internacionales.
            </p>

            {/* CTAs */}
            <div className="flex flex-wrap gap-3">
              <Link
                to="/catalogo"
                className="inline-flex items-center gap-2 px-6 py-3 bg-foreground-950 hover:bg-foreground-800 text-white text-sm font-semibold rounded-md transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-book-open-line"></i>
                Ver Catálogo de Productos
              </Link>
              <Link
                to="/contacto"
                className="inline-flex items-center gap-2 px-6 py-3 border border-primary-300 text-primary-600 hover:bg-primary-50 text-sm font-semibold rounded-md transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-chat-quote-line"></i>
                Solicitar Evaluación
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}